import { ApiError, ApiErrorData } from "@/types/apiTypes";

// Use a type assertion for import.meta.env
export const API_BASE_URL =
  // @ts-expect-error: Vite-specific environment variable
  import.meta.env?.VITE_API_URL || "http://localhost:3001";

/**
 * Handles API responses by checking if they're successful and parsing the JSON
 * @param response - The fetch Response object
 * @param defaultMessage - Default error message to use if none is provided by the API
 * @returns The parsed JSON response if successful
 * @throws ApiError if the response is not successful
 */
export async function handleApiResponse<T>(
  response: Response,
  defaultMessage: string = "API error occurred"
): Promise<T> {
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: defaultMessage }));

    throw new ApiError(errorData.error || defaultMessage, response, errorData);
  }

  return response.json();
}

/**
 * Handles API exceptions by checking if they're already ApiError instances
 * @param error - The caught error
 * @param defaultMessage - Default error message to use if the error is not an ApiError
 * @throws The original ApiError if the error is an ApiError, otherwise a new ApiError
 */
export function handleApiException(
  error: unknown,
  defaultMessage: string = "An unexpected error occurred"
): never {
  if (error instanceof ApiError) {
    throw error;
  }

  const errorData: ApiErrorData = {
    error: defaultMessage,
    serverError: true,
  };

  throw new ApiError(defaultMessage, undefined, errorData);
}
